import { HelpCircle } from 'lucide-react';
import { useTutorial } from './TutorialContext';

/**
 * TutorialLaunchButton — the "return to tour any time" entry point.
 *
 * Two visual modes:
 *   variant="icon"  — a floating "?" glyph, used in page headers.
 *   variant="text"  — an inline row for the Dashboard Settings panel.
 *
 * Both call `resetTutorial()` which flips the user's server-side
 * `tutorial_completed` back to false AND immediately opens the tour.
 * (Resetting the server flag also means the tour will auto-launch on
 * next login, which matches "restart my onboarding" intent.)
 */
const TutorialLaunchButton = ({ variant = 'icon', className = '' }) => {
  const { resetTutorial, scriptLoaded, steps } = useTutorial();

  // Only render if the user is authenticated (script loaded) and the tour
  // has steps to show. Prevents an orphan "?" glyph from appearing on
  // unauthenticated pages.
  if (!scriptLoaded || steps.length === 0) return null;

  if (variant === 'text') {
    return (
      <button
        type="button"
        onClick={resetTutorial}
        className={`text-xs text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-2 ${className}`}
        data-testid="tutorial-launch-text"
      >
        <HelpCircle className="w-3.5 h-3.5" />
        Take the tour
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={resetTutorial}
      className={`bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-sm w-9 h-9 flex items-center justify-center transition-all ${className}`}
      aria-label="Take the SAGE tour"
      data-testid="tutorial-launch-icon"
    >
      <HelpCircle className="w-4 h-4" />
    </button>
  );
};

export default TutorialLaunchButton;
